import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listFirmware,
  setFirmwareRemark,
  uploadFirmware,
  startRollout,
} from '../api/admin.js';
import type {
  AdminFirmwareVersion,
  FirmwareUploadBody,
} from '@m8-app/contracts/rest/admin.js';

export function Firmware() {
  return (
    <div className="p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Firmware Management</h2>

      <VersionsTable />

      <div className="grid grid-cols-2 gap-6 max-w-4xl mt-6">
        <UploadSection />
        <RolloutSection />
      </div>
    </div>
  );
}

function VersionsTable() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['firmwareVersions'],
    queryFn: () => listFirmware(),
  });

  if (isLoading) return <p className="text-sm text-gray-500">Loading…</p>;
  if (isError) {
    return <p className="text-sm text-red-600">Couldn't load firmware versions. Check your connection and try again.</p>;
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-600 text-left">
          <tr>
            <th className="px-4 py-3 font-medium">Version</th>
            <th className="px-4 py-3 font-medium">Family</th>
            <th className="px-4 py-3 font-medium">Published</th>
            <th className="px-4 py-3 font-medium">Remark</th>
            <th className="px-4 py-3 font-medium">Uploaded</th>
            <th className="px-4 py-3 font-medium">ID</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {data?.versions.map((v) => <VersionRow key={v.id} v={v} />)}
          {data && data.versions.length === 0 && (
            <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">No firmware versions yet</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function VersionRow({ v }: { v: AdminFirmwareVersion }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [remark, setRemark] = useState(v.remark ?? '');

  const { mutate, isPending } = useMutation({
    mutationFn: () => setFirmwareRemark(v.id, remark),
    onSuccess: () => { setEditing(false); qc.invalidateQueries({ queryKey: ['firmwareVersions'] }); },
  });

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3 font-mono">{v.version}</td>
      <td className="px-4 py-3">{v.controllerFamily}</td>
      <td className="px-4 py-3">{v.isPublished ? 'Yes' : 'No'}</td>
      <td className="px-4 py-3 max-w-[260px]">
        {editing ? (
          <div className="flex gap-1">
            <input value={remark} onChange={(e) => setRemark(e.target.value)} className="text-xs border border-gray-300 rounded px-2 py-1 flex-1"/>
            <button onClick={() => mutate()} disabled={isPending} className="text-xs px-2 py-1 bg-indigo-600 text-white rounded">{isPending ? '…' : 'Save'}</button>
            <button onClick={() => { setEditing(false); setRemark(v.remark ?? ''); }} className="text-xs px-2 py-1 text-gray-500">Cancel</button>
          </div>
        ) : (
          <button onClick={() => setEditing(true)} className="text-xs text-left text-gray-600 hover:text-indigo-600 truncate w-full">
            {v.remark || <span className="italic text-gray-400">add remark</span>}
          </button>
        )}
      </td>
      <td className="px-4 py-3 text-gray-400 text-xs">{v.createdAt.slice(0, 10)}</td>
      <td className="px-4 py-3 font-mono text-[10px] text-gray-500">{v.id}</td>
    </tr>
  );
}

// ── Upload ────────────────────────────────────────────────────────────────

function UploadSection() {
  const [form, setForm] = useState<Omit<FirmwareUploadBody, 'compatibleTypeIds'> & { compatibleTypeIds: string }>({
    controllerFamily: 'm8',
    version: '',
    compatibleTypeIds: '',
    artifactUrl: '',
    checksum: '',
    signature: '',
  });
  const [result, setResult] = useState<{ firmwareVersionId: string; isPublished: boolean } | null>(null);
  const [err, setErr] = useState('');

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      uploadFirmware({
        ...form,
        compatibleTypeIds: form.compatibleTypeIds
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      }),
    onSuccess: (res) => { setResult(res); setErr(''); },
    onError: (e: unknown) => setErr((e as { message?: string }).message ?? 'Upload failed'),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setResult(null);
    mutate();
  }

  function field(key: keyof typeof form, label: string, placeholder: string, type = 'text') {
    return (
      <div>
        <label htmlFor={`fw-${key}`} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <input
          id={`fw-${key}`}
          type={type}
          placeholder={placeholder}
          value={form[key]}
          onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          required
        />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">Upload Firmware</h3>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label htmlFor="fw-controllerFamily" className="block text-sm font-medium text-gray-700 mb-1">Controller Family</label>
          <select
            id="fw-controllerFamily"
            value={form.controllerFamily}
            onChange={(e) => setForm((f) => ({ ...f, controllerFamily: e.target.value as 'm8' | 'cop-lop' | 'avira' }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="m8">m8</option>
            <option value="cop-lop">cop-lop</option>
            <option value="avira">avira</option>
          </select>
        </div>
        {field('version', 'Version', '1.0.0')}
        {field('compatibleTypeIds', 'Compatible Type IDs', 'uuid1, uuid2 (comma-separated)')}
        {field('artifactUrl', 'Artifact URL', 'https://storage.example.com/fw.bin')}
        {field('checksum', 'Checksum (SHA-256)', 'abc123…')}
        {field('signature', 'Signature', 'sig…')}
        {err && <p className="text-sm text-red-600">{err}</p>}
        {result && (
          <div className="text-sm text-green-700 bg-green-50 rounded px-3 py-2">
            <p>Uploaded — ID: <code className="font-mono text-xs">{result.firmwareVersionId}</code></p>
            <p>Published: {result.isPublished ? 'Yes' : 'No'}</p>
          </div>
        )}
        <button
          type="submit"
          disabled={isPending}
          className="w-full bg-indigo-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
        >
          {isPending ? 'Uploading…' : 'Upload'}
        </button>
      </form>
    </div>
  );
}

// ── Rollout ───────────────────────────────────────────────────────────────

function RolloutSection() {
  const [fwId, setFwId] = useState('');
  const [mode, setMode] = useState<'all' | 'specific'>('all');
  const [deviceIds, setDeviceIds] = useState('');
  const [result, setResult] = useState<{ rolloutId: string; state: string } | null>(null);
  const [err, setErr] = useState('');

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      startRollout(fwId, {
        ...(mode === 'specific'
          ? { deviceIds: deviceIds.split('\n').map((s) => s.trim()).filter(Boolean) }
          : {}),
      }),
    onSuccess: (res) => { setResult(res); setErr(''); },
    onError: (e: unknown) => setErr((e as { message?: string }).message ?? 'Rollout failed'),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setResult(null);
    mutate();
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">Start Rollout</h3>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label htmlFor="rolloutFwId" className="block text-sm font-medium text-gray-700 mb-1">Firmware Version ID</label>
          <input
            id="rolloutFwId"
            type="text"
            placeholder="UUID from upload"
            value={fwId}
            onChange={(e) => setFwId(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            required
          />
        </div>
        <div>
          <p className="block text-sm font-medium text-gray-700 mb-1">Target mode</p>
          <div className="flex gap-4">
            <label className="flex items-center gap-1.5 text-sm cursor-pointer">
              <input type="radio" name="mode" value="all" checked={mode === 'all'} onChange={() => setMode('all')} />
              All eligible devices
            </label>
            <label className="flex items-center gap-1.5 text-sm cursor-pointer">
              <input type="radio" name="mode" value="specific" checked={mode === 'specific'} onChange={() => setMode('specific')} />
              Specific devices
            </label>
          </div>
        </div>
        {mode === 'all' && (
          <p className="text-xs text-gray-400 bg-gray-50 rounded-lg px-3 py-2">
            Rollout will be sent to all active devices matching this firmware&apos;s controller family and compatible type IDs. Only devices currently online and on an older version will be updated.
          </p>
        )}
        {mode === 'specific' && (
          <div>
            <label htmlFor="deviceIdList" className="block text-sm font-medium text-gray-700 mb-1">Device IDs (one per line)</label>
            <textarea
              id="deviceIdList"
              rows={4}
              value={deviceIds}
              onChange={(e) => setDeviceIds(e.target.value)}
              placeholder="device-id-1&#10;device-id-2"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono"
              required
            />
          </div>
        )}
        {err && <p className="text-sm text-red-600">{err}</p>}
        {result && (
          <div className="text-sm text-green-700 bg-green-50 rounded px-3 py-2">
            <p>Rollout started — ID: <code className="font-mono text-xs">{result.rolloutId}</code></p>
            <p>State: <span className="font-medium">{result.state}</span></p>
          </div>
        )}
        <button
          type="submit"
          disabled={isPending}
          className="w-full bg-indigo-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
        >
          {isPending ? 'Starting…' : 'Start Rollout'}
        </button>
      </form>
    </div>
  );
}
